import ChevronRightOutlined from "@mui/icons-material/ChevronRightOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import FileUploadRounded from "@mui/icons-material/FileUploadRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import size from "lodash/size";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { columnTypesDate } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesNumber } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesNumber";

import { useFileDocGroupAndTypesHook } from "../../hooks/useFileDocGroupAndTypesHook";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";
import LoadingFallback from "../LoadingFallback";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import FileUploadField from "../inputs/FileUploadField";
import SelectFileAssignmentInput from "../inputs/SelectFileAssignmentInput";
import SelectInput from "../inputs/SelectInput";
import Flx from "../layout/Flx";
import BasicModal from "../modals/BasicModal";
import Txt from "../typography/Txt";
import FilesListRenderer from "./FilesListRenderer";

const FileUploader = ({
  onClose,
  defaultDocGroup,
  defaultDocType,
  height,
  borderSize = 1.5,
  sx,
}) => {
  const [files, setFiles] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [docGroup, setDocGroup] = useState(defaultDocGroup);
  const [docType, setDocType] = useState(defaultDocType);
  const [step, setStep] = useState(0);

  const fileDocGroupAndTypes = useSelector(
    (state) => state.fetchedSelections?.fileDocGroupAndTypes,
  );

  const onModalClose = useCallback(
    (props) => {
      setFiles(null);
      setIsModalOpen(false);
      setDocGroup(defaultDocGroup);
      setDocType(defaultDocType);
      setStep(0);

      if (isFunction(onClose)) {
        onClose(props);
      }
    },
    [
      setFiles,
      setIsModalOpen,
      setDocGroup,
      setDocType,
      setStep,
      onClose,
      defaultDocGroup,
      defaultDocType,
    ],
  );

  const updateFilesMetadata = (files, docGroup, docType) => {
    if (!files || files.length === 0) {
      return [];
    }

    return files.map((file) => {
      const updatedFile = { ...file };

      if (docGroup !== undefined && docGroup !== null) {
        updatedFile.docGroup = docGroup;

        if (updatedFile.docType && docType === undefined) {
          updatedFile.docType = undefined;
        }
      }

      if (docType !== undefined && docType !== null) {
        updatedFile.docType = docType;
      }

      return updatedFile;
    });
  };

  const onFilesAdded = useCallback(
    (newFiles, docGroup, docType) => {
      let updatedFiles = updateFilesMetadata(newFiles, docGroup, docType);
      if (isEmpty(files)) {
        setFiles(updatedFiles);
        return;
      }

      setFiles([...files, ...updatedFiles]);
    },
    [files],
  );

  const onDocGroupTypeChange = useCallback(
    (dg, dt) => {
      let updatedFiles = updateFilesMetadata(files, dg, dt);
      setDocGroup(dg);
      setDocType(dt);
      if (isEmpty(files)) {
        setFiles(updatedFiles);
        return;
      }

      setFiles(updatedFiles);
      setStep((p) => p + 1);
    },
    [files, setFiles],
  );

  const handleInitialFileUpload = useCallback(
    (uploadedFiles) => {
      if (!isModalOpen && uploadedFiles && uploadedFiles.length > 0) {
        setFiles(uploadedFiles);
        setIsModalOpen(true);
      }
    },
    [isModalOpen],
  );

  const handleModifyDocGroupType = useCallback((docGroup, docType) => {
    setDocGroup(docGroup);
    setDocType(docType);
  }, []);

  useEffect(() => {
    setDocGroup(defaultDocGroup);
    setDocType(defaultDocType);
  }, [defaultDocGroup, defaultDocType]);

  return (
    <>
      <FileUploadField
        key="initial-upload"
        id="initial-file-upload"
        suppressUploadButton
        borderSize={borderSize}
        hideFileList
        minHeight={height}
        sx={sx}
        onFilesAdded={handleInitialFileUpload}
      />
      <BasicModal
        allowFullScreen
        title={"File Upload"}
        show={isModalOpen && isArray(files)}
        onClose={onModalClose}
        maxWidth={"md"}
        bodySx={{
          g: 0,
          p: 0,
          ".MuiSelect-select": {
            fontSize: "12px",
          },
        }}
      >
        {isNil(fileDocGroupAndTypes) ? (
          <LoadingFallback sx={{ height: "300px" }} />
        ) : (
          <>
            <DocumentAssignmentContent
              setFiles={setFiles}
              files={files}
              step={step}
              docGroup={docGroup}
              docType={docType}
              setDocGroup={setDocGroup}
              setDocType={setDocType}
              onFilesAdded={onFilesAdded}
              modifyDocGroupType={handleModifyDocGroupType}
              onDocGroupTypeChange={onDocGroupTypeChange}
            />
            <Footer
              step={step}
              setStep={setStep}
              files={files}
              onClear={onModalClose}
            />
          </>
        )}
      </BasicModal>
    </>
  );
};

const Footer = ({ step, setStep, files, onClear }) => {
  const dispatch = useDispatch();
  const { loading, uploadLoanDocs } = useLoanFilesHook();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const handleUploadFiles = () => {
    uploadLoanDocs({
      loanId: loanDrilldown?._id,
      files: files,
      onSuccessMsg:
        size(files) > 1
          ? `${size(files)} files uploaded successfully`
          : "File uploaded successfully",
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        onClear();
      },
    });
  };
  return (
    <Flx
      g={1}
      fw
      jb
      ac
      wrap
      sx={{
        mt: 0,
        p: 2,
        borderTop: "1px solid #e0e0e0",
      }}
    >
      <Box />
      <Flx wrap center gap={1}>
        {step === 1 ? (
          <Button
            onClick={onClear}
            variant="outlined"
            size="large"
            color="inherit"
          >
            Clear All
          </Button>
        ) : null}

        {step === 0 ? (
          <Button
            onClick={() => setStep((p) => p + 1)}
            endIcon={<ChevronRightOutlined className="thin" />}
            variant="outlined"
            size="large"
            color="primary"
          >
            Next
          </Button>
        ) : (
          <Button
            loading={loading}
            sx={{ flexGrow: 1, flexBasis: "160px" }}
            onClick={handleUploadFiles}
            startIcon={
              <FileUploadRounded
                className="thin"
                sx={{ fontSize: "16px !important" }}
              />
            }
            size="large"
            color="primary"
          >
            Upload Files ({size(files)})
          </Button>
        )}
      </Flx>
    </Flx>
  );
};

const DocumentAssignmentContent = ({
  files,
  step,

  setFiles,
  docType,
  docGroup,
  setDocGroup,
  setDocType,
  onFilesAdded,
  onDocGroupTypeChange,
  modifyDocGroupType,
}) => {
  return (
    <Flx column fw>
      {step === 0 && (
        <AssignmentStep
          files={files}
          setFiles={setFiles}
          docGroup={docGroup}
          docType={docType}
          setDocGroup={setDocGroup}
          setDocType={setDocType}
          onFilesAdded={onFilesAdded}
          onDocGroupTypeChange={onDocGroupTypeChange}
        />
      )}
      {step === 1 && (
        <TableEditingStep
          files={files}
          setFiles={setFiles}
          onFilesAdded={onFilesAdded}
          docGroup={docGroup}
          docType={docType}
          modifyDocGroupType={modifyDocGroupType}
        />
      )}
    </Flx>
  );
};

const AssignmentStep = ({
  files,
  setFiles,
  docType,
  docGroup,
  onDocGroupTypeChange,
}) => {
  return (
    <Flx column fw sx={{ p: 3, pb: 4 }}>
      <Flx column sx={{ mb: 6 }}>
        <Txt sx={{ fontWeight: 600, fontSize: "14px", mb: 1 }}>
          Uploaded Files
        </Txt>
        <FilesListRenderer files={files} onFileDelete={setFiles} />
      </Flx>

      <Flx column>
        <Txt sx={{ fontWeight: 600, fontSize: "14px", mb: 1 }}>
          Document Type:
        </Txt>
        <SelectFileAssignmentInput
          placeholder="Search document categories and types..."
          onChange={(params) =>
            onDocGroupTypeChange(params?.docGroup, params?.docType)
          }
          docGroup={docGroup}
          docType={docType}
          fullWidth
        />
      </Flx>
    </Flx>
  );
};

const TableEditingStep = ({
  files,
  setFiles,
  docType,
  docGroup,
  onFilesAdded,
  modifyDocGroupType,
}) => {
  // Use a ref to track if we're in this component
  const isInTableStep = useRef(true);

  // Handle adding additional files - only process new files
  const handleAdditionalFiles = useCallback(
    (newFiles) => {
      if (!newFiles || newFiles.length === 0) return;

      onFilesAdded(newFiles, docGroup, docType);
    },
    [onFilesAdded, docGroup, docType],
  );

  useEffect(() => {
    return () => {
      isInTableStep.current = false;
    };
  }, []);

  return (
    <>
      <Box p={2} pb={2}>
        <FileUploadField
          key="additional-upload"
          id="additional-file-upload"
          uploadText="Drag & Drop additional files here"
          suppressUploadButton
          borderSize={1.5}
          height="80px"
          hideFileList
          onFilesAdded={handleAdditionalFiles}
          docGroup={docGroup}
          docType={docType}
        />
        <SelectFileAssignmentInput
          sx={{ mt: 1 }}
          placeholder="Select document type..."
          onChange={(params) =>
            modifyDocGroupType(params?.docGroup, params?.docType)
          }
          docGroup={docGroup}
          docType={docType}
          fullWidth
        />
      </Box>
      <UploadedFilesTableContent files={files} setFiles={setFiles} />
    </>
  );
};

const UploadedFilesTableContent = ({ files, setFiles }) => {
  const ref = useRef();

  const modalContentRef = useRef(null);
  const { getFileDocGroups, getDocTypesByGroup } =
    useFileDocGroupAndTypesHook();
  const [selectedRows, setSelectedRows] = useState(null);
  const fileDocGroupAndTypes = useSelector(
    (state) => state.fetchedSelections?.fileDocGroupAndTypes,
  );
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Filename",
        field: "file_display_name",
        flex: 1,
        minWidth: 250,
        cellEditor: "agTextCellEditor",
        editable: true,
        checkbox: true,
      },
      {
        headerName: "Document Group",
        field: "docGroup",
        editable: true,
        width: 220,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        cellEditorParams: {
          values: getFileDocGroups(fileDocGroupAndTypes) || [],
          // values: fileDocGroups || [],
        },
      },
      {
        headerName: "Document Type",
        field: "docType",
        editable: true,
        width: 220,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        cellEditorParams: (params) => {
          return {
            values: getDocTypesByGroup(
              params.data.docGroup || "Appraisal",
              fileDocGroupAndTypes,
            ),
          };
        },
      },
    ],
    [getFileDocGroups, getDocTypesByGroup, fileDocGroupAndTypes],
  );

  const handleRowSelectionChange = useCallback((params) => {
    const selected = params.api.getSelectedRows();
    setSelectedRows(selected);
  }, []);

  // Handle deleting the selected rows
  const handleDeleteSelectedRows = useCallback(() => {
    if (!selectedRows || selectedRows.length === 0) return;

    const selectedIds = selectedRows.map((r) => r.id);

    // Update the parent files state by filtering out deleted rows
    setFiles((prev) => prev.filter((row) => !selectedIds.includes(row.id)));

    if (ref.current && ref.current.api) {
      ref.current.api.deselectAll();
    }

    setSelectedRows([]);
  }, [selectedRows, setFiles]);

  const handleDeselectAllRows = useCallback(() => {
    if (ref.current && ref.current.api) {
      ref.current.api.deselectAll();
    }
    setSelectedRows([]);
  }, []);

  const handleCellValueChanged = useCallback(
    (params) => {
      const { colDef, data, newValue } = params;

      // Create a copy of the data to update
      const updatedData = { ...data };

      // If the user changes the filename
      if (colDef.field === "file_display_name") {
        const originalExtension = data.name.split(".").pop().toLowerCase();
        const newValueWithoutExt = newValue.replace(/\.[^/.]+$/, "");
        updatedData.file_display_name = `${newValueWithoutExt}.${originalExtension}`;
      }

      // If the user changes docGroup
      if (colDef.field === "docGroup") {
        updatedData.docGroup = newValue;
        // Clear docType when docGroup changes
        updatedData.docType = null;
        // updatedData.docType = "";
      }

      // If the user changes docType
      if (colDef.field === "docType") {
        updatedData.docType = newValue;
      }

      // Update the parent files state
      setFiles((prev) =>
        prev.map((item) => (item.id === data.id ? updatedData : item)),
      );

      setTimeout(() => {
        if (ref.current && ref.current.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [setFiles],
  );

  const handleMetadataUpdate = useCallback(
    ({
      newDocGroup,
      newDocType,
      newEffectiveDate,
      newGuidelineExpirationDays,
    }) => {
      if (!selectedRows || selectedRows.length === 0) return;

      const selectedRowIds = selectedRows.map((row) => row.id);

      setFiles((prev) => {
        return prev.map((row) => {
          if (selectedRowIds.includes(row.id)) {
            const updatedRow = { ...row };

            if (newDocGroup !== undefined) {
              updatedRow.docGroup = newDocGroup;
              updatedRow.docType = null;
              // updatedRow.docType = "";
            }

            if (newDocType !== undefined && newDocGroup === undefined) {
              updatedRow.docType = newDocType || "";
            }

            if (newEffectiveDate !== undefined) {
              if (newEffectiveDate instanceof Date) {
                updatedRow.reportEffectiveDate = newEffectiveDate.toISOString();
              } else {
                updatedRow.reportEffectiveDate = newEffectiveDate;
              }
            }

            if (newGuidelineExpirationDays !== undefined) {
              updatedRow.reportGuidelineExpirationDays =
                newGuidelineExpirationDays;
            }

            return updatedRow;
          }
          return row;
        });
      });

      setTimeout(() => {
        if (ref.current && ref.current.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [selectedRows, setFiles],
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      ...columnTypesNumber(),
    };
  }, []);

  // Ensure files have IDs for AG-Grid
  const rowData = useMemo(() => {
    return files.map((file, index) => ({
      ...file,
      id: file.id || `${Date.now()}-${index}-${file.name}`,
      file_display_name: file.file_display_name || file.name,
    }));
  }, [files]);

  if (isEmpty(files)) {
    return null;
  }

  return (
    <Flx flexGrow={1} column gap={0}>
      {isEmpty(selectedRows) ? null : (
        <Flx fw jb ac>
          <BulkActionsToolbar
            selectedRows={selectedRows}
            onDeleteSelectedRows={handleDeleteSelectedRows}
            onMetadataUpdate={handleMetadataUpdate}
            onDeselectAllRows={handleDeselectAllRows}
          />
        </Flx>
      )}

      <div ref={modalContentRef}>
        <AgGridTableWrapper height={"340px"} simpleTable size={4}>
          <AgGridReact
            ref={ref}
            getRowId={(params) => params.data.id}
            rowData={rowData}
            columnDefs={columnDefs}
            singleClickEdit
            columnTypes={columnTypes}
            onSelectionChanged={handleRowSelectionChange}
            // domLayout={"autoHeight"}
            onCellValueChanged={handleCellValueChanged}
            stopEditingWhenCellsLoseFocus
            popupParent={modalContentRef.current}
            rowSelection={{
              mode: "multiRow",
            }}
          />
        </AgGridTableWrapper>
      </div>
    </Flx>
  );
};

const BulkActionsToolbar = ({
  selectedRows,
  onDeselectAllRows,
  onDeleteSelectedRows,
  onMetadataUpdate,
}) => {
  const [bulkDocGroup, setBulkDocGroup] = useState(null);
  const [bulkDocType, setBulkDocType] = useState(null);
  // const [bulkEffectiveDate, setBulkEffectiveDate] = useState("");
  // const [bulkNumExpirationDays, setBulkNumExpirationDays] = useState(null);

  const {
    fileDocGroupAndTypes,
    fileDocGroups,
    getDocTypesByGroup,
    getDocumentTypeOptions,
    searchDocumentsAdvanced,
  } = useFileDocGroupAndTypesHook();

  const onGroupChange = (group) => {
    setBulkDocGroup(group);
    onMetadataUpdate({ newDocGroup: group });
  };

  const onTypeChange = (type) => {
    setBulkDocType(type);
    onMetadataUpdate({ newDocType: type });
  };

  // const onEffectiveDateChange = (date) => {
  //   setBulkEffectiveDate(date);
  //   onMetadataUpdate({ newEffectiveDate: date });
  // };

  // const onNumExpirationDaysChange = (num) => {
  //   setBulkNumExpirationDays(num);
  //   onMetadataUpdate({ newGuidelineExpirationDays: num });
  // };

  useEffect(() => {
    if (isEmpty(selectedRows)) {
      setBulkDocGroup(null);
      setBulkDocType(null);
      // setBulkEffectiveDate(null);
      // setBulkNumExpirationDays(null);
    }
  }, [selectedRows]);

  if (isEmpty(selectedRows)) {
    return null;
  }

  return (
    <Flx
      fw
      jb
      wrap
      ac
      gap={1}
      sx={{
        background: grey[100],
        p: 1.2,
        zIndex: "1111",
        ".MuiSelect-select, .MuiInputBase-root, .txt": {
          fontSize: "12px",
        },
      }}
    >
      <PseudoColumn sx={{ alignItems: "center" }} grow={1} gap={1}>
        <Tooltip
          placement="top-start"
          title={`Deselect All`}
          disableInteractive
        >
          <IconButton
            onClick={onDeselectAllRows}
            variant="outlined"
            size="small"
          >
            <CloseOutlined className="thin" />
          </IconButton>
        </Tooltip>
        <Txt sx={{ lineHeight: "normal" }} bold>
          {`Selected Rows (${size(selectedRows)})`}
        </Txt>
        <Tooltip
          title={
            size(selectedRows) === 1
              ? "Delete File"
              : `Delete ${size(selectedRows)} Files`
          }
          disableInteractive
        >
          <IconButton
            onClick={onDeleteSelectedRows}
            color="error"
            variant="outlined"
            size="small"
          >
            <DeleteOutlineOutlined className="thin" />
          </IconButton>
        </Tooltip>
      </PseudoColumn>
      <PseudoColumn width="205px">
        <SelectInput
          value={bulkDocGroup}
          placeholder="Document Group"
          options={fileDocGroups}
          onChange={onGroupChange}
        />
      </PseudoColumn>
      <PseudoColumn width="210px">
        {isNil(bulkDocGroup) ? null : (
          <SelectInput
            value={bulkDocType}
            placeholder="Document Type"
            options={getDocTypesByGroup(bulkDocGroup) || []}
            onChange={onTypeChange}
          />
        )}
      </PseudoColumn>
    </Flx>
  );
};

/**
 * SHARED COMPONENTS
 */

const PseudoColumn = ({ sx, center, grow = 0, gap, width, children }) => (
  <Flx
    center={center}
    flexShrink={0}
    gap={gap}
    flexGrow={grow}
    sx={{
      width: width,
      flexBasis: width,
      ...sx,
    }}
  >
    {children}
  </Flx>
);

export default FileUploader;
